export const TRIAL_DURATION_DAYS = 14;
export const TRIAL_STORAGE_KEY = 'trialData';

export interface TrialData {
  startDate: string;
  isActive: boolean;
  userData: {
    preferences: Record<string, unknown>;
    savedItems: unknown[];
    lastAccessed: string;
  };
}

export interface TrialStatus {
  isActive: boolean;
  daysRemaining: number;
  hasExpired: boolean;
  features: string[];
}

export const TRIAL_FEATURES = [
  'assessment',
  'monitoring',
  'reports',
  'support',
  'family_access'
] as const;

export type TrialFeature = typeof TRIAL_FEATURES[number];

const getStoredTrialData = (): TrialData | null => {
  const stored = localStorage.getItem(TRIAL_STORAGE_KEY);
  return stored ? JSON.parse(stored) : null;
};

export const getTrialStatus = (): TrialStatus => {
  const trialData = getStoredTrialData();

  if (!trialData) {
    return { 
      isActive: false, 
      daysRemaining: 0, 
      hasExpired: false,
      features: [] 
    };
  }

  const startDate = new Date(trialData.startDate);
  const currentDate = new Date();
  const diffTime = currentDate.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const daysRemaining = TRIAL_DURATION_DAYS - diffDays;

  const isActive = daysRemaining > 0 && trialData.isActive;
  
  return {
    isActive,
    daysRemaining: Math.max(0, daysRemaining),
    hasExpired: daysRemaining <= 0 && trialData.isActive,
    features: isActive ? [...TRIAL_FEATURES] : []
  };
};

export const startTrial = () => {
  const trialData: TrialData = {
    startDate: new Date().toISOString(),
    isActive: true,
    userData: {
      preferences: {},
      savedItems: [],
      lastAccessed: new Date().toISOString()
    }
  };
  
  localStorage.setItem(TRIAL_STORAGE_KEY, JSON.stringify(trialData));
  console.log('Trial started:', getTrialStatus());
};

export const updateTrialData = (userData: Partial<TrialData['userData']>) => {
  const currentData = getStoredTrialData();
  if (!currentData) return false;

  const updatedData = {
    ...currentData,
    userData: {
      ...currentData.userData,
      ...userData,
      lastAccessed: new Date().toISOString()
    }
  };

  localStorage.setItem(TRIAL_STORAGE_KEY, JSON.stringify(updatedData));
  return true;
};

export const clearTrialData = () => {
  localStorage.removeItem(TRIAL_STORAGE_KEY);
};

export const hasFeatureAccess = (feature: TrialFeature): boolean => {
  const status = getTrialStatus();
  return status.isActive && status.features.includes(feature);
};

export const preserveTrialData = async () => {
  const trialData = getStoredTrialData();
  if (!trialData) return null;

  // Here you would typically send this to your backend
  // For now, we'll just return the data
  return {
    userData: trialData.userData,
    trialEndDate: new Date(new Date(trialData.startDate).getTime() + TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000).toISOString()
  };
};